import { thumbnailDisplayUrl } from "@/lib/venueImages";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useThemeContext } from './ThemeContext';

const PREVIEW_MAX = 120;

function formatPreview(preview: string): string {
  const text = preview.replace(/\s+/g, ' ').trim();
  if (text.length <= PREVIEW_MAX) return text;
  return `${text.slice(0, PREVIEW_MAX)}…`;
}

function Avatar({ name, avatar }: { name: string; avatar: string | null }) {
  const [failed, setFailed] = useState(false);
  if (avatar && !failed) {
    return (
      <img
        src={thumbnailDisplayUrl(avatar, 160)}
        alt=""
        referrerPolicy="no-referrer"
        className="w-10 h-10 rounded-full object-cover shrink-0"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold bg-gradient-to-br from-[#ff3366] to-pink-500 text-white">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * Global toast when a chat message arrives while the user is not viewing that thread.
 */
export function ChatIncomingMessageNotifier() {
  const navigate = useNavigate();
  const { isLight } = useThemeContext();
  const sessionReady = useAuthStore((s) => s.sessionReady);
  const accountType = useAuthStore((s) => s.user?.accountType);
  const alert = useChatStore((s) => s.incomingMessageAlert);
  const alertSeq = useChatStore((s) => s.incomingMessageAlertSeq);

  useEffect(() => {
    if (!sessionReady || accountType !== 'member' || !alert || alertSeq === 0) return;

    const { conversationId, senderName, senderAvatar, preview } = alert;
    const cardCls = isLight
      ? 'bg-white/95 border-zinc-200/80 text-zinc-900'
      : 'bg-[#12121c]/95 border-white/10 text-white';
    const mutedCls = isLight ? 'text-zinc-500' : 'text-zinc-400';
    const secondaryBtnCls = isLight
      ? 'bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200'
      : 'bg-white/[0.06] border border-white/10 text-gray-200 hover:bg-white/[0.1]';

    toast.custom(
      (id) => (
        <div className={`flex items-start gap-3 rounded-xl border shadow-2xl px-4 py-3.5 w-[min(360px,90vw)] backdrop-blur-xl ${cardCls}`}>
          <Avatar name={senderName} avatar={senderAvatar} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug flex items-center gap-1.5">
              <MessageCircle size={14} className="text-[#ff3366] shrink-0" />
              {senderName}
            </p>
            <p className={`text-xs mt-0.5 leading-snug ${mutedCls}`}>{formatPreview(preview)}</p>
            <div className="flex items-center gap-2 mt-2.5">
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(id);
                  navigate(`/member/chat?view=inbox&conversation=${conversationId}`, {
                    preventScrollReset: true,
                  });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#ff3366] to-pink-500 text-white hover:opacity-90 transition-opacity"
              >
                View
              </button>
              <button
                type="button"
                onClick={() => toast.dismiss(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${secondaryBtnCls}`}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ),
      { duration: 8000, unstyled: true }
    );
  }, [sessionReady, accountType, alert, alertSeq, isLight, navigate]);

  return null;
}
